import { createApi } from "@reduxjs/toolkit/query/react";

import { LogicalType, PhysicalType, type FieldDTO } from "../../../types/Field";
import { FieldHistoryDTO } from "../../../types/FieldHistory";
import { datasetService } from "../datasets";
import { getBaseQuery } from "../baseQuery";

export const fieldService = createApi({
  reducerPath: "fieldService",
  baseQuery: getBaseQuery(),
  tagTypes: ["Fields", "FieldHistory"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    updateFieldById: builder.mutation<FieldDTO, { field: FieldDTO }>({
      query: (arg) => {
        const { field } = arg;
        return {
          url: `/orpheus/api/fields/${field.id}`,
          method: "PUT",
          body: field,
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "FieldHistory", id: arg.field.id },
      ],
      //TODO: Make use of redux code splitting so all metadata endpoints exists in same slice and remove need for this
      onQueryStarted(_arg, api): void {
        void api.queryFulfilled.then(() => {
          api.dispatch(
            datasetService.util.invalidateTags([
              { type: "Datasets", id: "LIST" },
              { type: "Datasets", id: "DETAIL" },
            ]),
          );
        });
      },
    }),
    createField: builder.mutation<FieldDTO, { field: FieldDTO }>({
      query: (arg) => {
        const { field } = arg;
        return {
          url: `/orpheus/api/fields`,
          method: "POST",
          body: field,
        };
      },
      //TODO: Make use of redux code splitting so all metadata endpoints exists in same slice and remove need for this
      onQueryStarted(_arg, api): void {
        void api.queryFulfilled.then(() => {
          api.dispatch(
            datasetService.util.invalidateTags([
              { type: "Datasets", id: "LIST" },
              { type: "Datasets", id: "DETAIL" },
            ]),
          );
        });
      },
    }),
    deleteField: builder.mutation<void, { fieldId: string }>({
      query: (arg) => {
        const { fieldId } = arg;
        return {
          url: `/orpheus/api/fields/${fieldId}`,
          method: "DELETE",
        };
      },
      //TODO: Make use of redux code splitting so all metadata endpoints exists in same slice and remove need for this
      onQueryStarted(_arg, api): void {
        void api.queryFulfilled.then(() => {
          api.dispatch(
            datasetService.util.invalidateTags([
              { type: "Datasets", id: "LIST" },
              { type: "Datasets", id: "DETAIL" },
            ]),
          );
        });
      },
    }),
    inferFieldTypes: builder.mutation<
      {
        columns: {
          [name: string]: {
            logical_type: LogicalType;
            physical_type: PhysicalType;
          };
        };
      },
      { columns: (string | number)[]; rows: any[][] }
    >({
      query: (arg) => {
        const { columns, rows } = arg;
        return {
          url: `/hermes/api/infer_field_types`,
          method: "POST",
          body: {
            columns,
            rows,
          },
        };
      },
    }),
    getFieldHistory: builder.query<FieldHistoryDTO[], { fieldId: string }>({
      query: (arg) => {
        const { fieldId } = arg;
        return {
          url: `/orpheus/api/fields/${fieldId}/history`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, arg) => [{ type: "FieldHistory", id: arg.fieldId }],
    }),
    restoreFieldMetadata: builder.mutation<
      void,
      { fieldId: string; targetVersion: number }
    >({
      query: (arg) => {
        const { fieldId, targetVersion } = arg;
        return {
          url: `/orpheus/api/fields/${fieldId}/metadata-restore`,
          method: "POST",
          body: { targetVersion },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "FieldHistory", id: arg.fieldId },
      ],
      onQueryStarted(_arg, api): void {
        void api.queryFulfilled.then(() => {
          api.dispatch(
            datasetService.util.invalidateTags([
              { type: "Datasets", id: "LIST" },
              { type: "Datasets", id: "DETAIL" },
            ]),
          );
        });
      },
    }),
  }),
});

export const {
  useUpdateFieldByIdMutation,
  useCreateFieldMutation,
  useDeleteFieldMutation,
  useInferFieldTypesMutation,
  useGetFieldHistoryQuery,
  useRestoreFieldMetadataMutation,
} = fieldService;
